type StorageEntry = {
  key: string
  value: string
  size: number
  timestamp: number
  isExpired?: boolean
  isCritical?: boolean
}

type ExpiringStorageObject = {
  value: string
  expiryStamp: number
  timestamp?: number
}

class LocalStorageManager {
  private static readonly QUOTA_THRESHOLD = 0.75 // 75% of quota - keep well below browser limit
  private static readonly CRITICAL_KEYS = [
    'browser-tabs-lock-key',
    'REFRESH_TOKEN_USE',
    'ACCESS_TOKEN_USE',
    'supertokens-',
    'layoutSetting',
  ]

  /**
   * Estimates current localStorage usage in bytes
   */
  private static getStorageSize(): number {
    let total = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const value = localStorage.getItem(key) || ''
        total += key.length + value.length
      }
    }
    return total
  }

  /**
   * Estimates localStorage quota (varies by browser, typically 5-10MB)
   */
  private static getStorageQuota(): number {
    // Conservative estimate - most browsers allow 5MB minimum
    return 5 * 1024 * 1024 // 5MB in bytes
  }

  /**
   * Gets all localStorage entries with metadata
   */
  private static getAllEntries(): StorageEntry[] {
    const entries: StorageEntry[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue

      const value = localStorage.getItem(key) || ''
      const size = key.length + value.length
      const isCritical = this.CRITICAL_KEYS.some((criticalKey) => key.includes(criticalKey))

      let timestamp = Date.now()
      let isExpired = false

      // Try to extract timestamp and expiry from value
      try {
        const parsed = JSON.parse(value) as ExpiringStorageObject
        if (parsed.expiryStamp) {
          isExpired = Date.now() > parsed.expiryStamp
        }
        if (parsed.timestamp) {
          timestamp = parsed.timestamp
        }
      } catch {
        // Not a structured object, use current time
      }

      entries.push({
        key,
        value,
        size,
        timestamp,
        isExpired,
        isCritical,
      })
    }

    return entries
  }

  /**
   * Removes expired and old entries to free up space
   */
  private static forceEviction(targetBytes: number): number {
    const entries = this.getAllEntries()
    let freedBytes = 0

    // Sort by eviction priority:
    // 1. Expired entries first
    // 2. Then non-critical entries by oldest timestamp
    // 3. Then by largest size
    const sortedEntries = entries
      .filter((entry) => !entry.isCritical) // Never evict critical entries
      .sort((a, b) => {
        // Expired entries first
        if (a.isExpired && !b.isExpired) return -1
        if (!a.isExpired && b.isExpired) return 1

        // Then by timestamp (oldest first)
        if (a.timestamp !== b.timestamp) {
          return a.timestamp - b.timestamp
        }

        // Then by size (largest first)
        return b.size - a.size
      })

    // Remove entries until we've freed enough space
    for (const entry of sortedEntries) {
      if (freedBytes >= targetBytes) break

      try {
        localStorage.removeItem(entry.key)
        freedBytes += entry.size
        console.log(`[LocalStorageManager] Evicted "${entry.key}" (${entry.size} bytes)`)
      } catch (_error) {
        console.warn(`[LocalStorageManager] Failed to evict "${entry.key}":`, _error)
      }
    }

    return freedBytes
  }

  /**
   * Auto-cleanup: removes expired entries and evicts if over threshold
   * Called on every access to keep storage clean
   */
  private static autoCleanup(requiredBytes: number = 0): boolean {
    // Always clean expired entries first
    this.cleanupExpired()

    const currentSize = this.getStorageSize()
    const quota = this.getStorageQuota()
    const usageRatio = currentSize / quota

    // If we're over threshold or don't have space for required bytes
    if (
      usageRatio > this.QUOTA_THRESHOLD ||
      currentSize + requiredBytes > quota * this.QUOTA_THRESHOLD
    ) {
      const targetFreeBytes = Math.max(
        quota * (1 - this.QUOTA_THRESHOLD) - (quota - currentSize), // Free to get back under threshold
        requiredBytes * 2 // Or double the required bytes for buffer
      )

      const freedBytes = this.forceEviction(targetFreeBytes)
      return freedBytes > 0
    }

    return true // No cleanup needed
  }

  /**
   * Safe wrapper for localStorage.setItem with automatic cleanup
   */
  static setItem(key: string, value: string): boolean {
    const requiredBytes = key.length + value.length

    // Auto-cleanup before writing
    this.autoCleanup(requiredBytes)

    try {
      localStorage.setItem(key, value)
      return true
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn(
          `[LocalStorageManager] Quota exceeded, attempting aggressive cleanup for "${key}"`
        )

        // Aggressive eviction as fallback
        this.forceEviction(requiredBytes * 3)

        try {
          localStorage.setItem(key, value)
          return true
        } catch (retryError) {
          console.error(
            `[LocalStorageManager] Failed to store "${key}" even after cleanup:`,
            retryError
          )
          return false
        }
      }

      console.error(`[LocalStorageManager] Failed to store "${key}":`, error)
      return false
    }
  }

  /**
   * Safe wrapper for localStorage.getItem
   */
  static getItem(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      console.error(`[LocalStorageManager] Failed to get "${key}":`, error)
      return null
    }
  }

  /**
   * Safe wrapper for localStorage.removeItem
   */
  static removeItem(key: string): boolean {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`[LocalStorageManager] Failed to remove "${key}":`, error)
      return false
    }
  }

  /**
   * Sets item with expiry timestamp for automatic cleanup
   */
  static setItemWithExpiry(key: string, value: string, ttlMs: number = 86400000): boolean {
    const expiringObject: ExpiringStorageObject = {
      value,
      expiryStamp: Date.now() + ttlMs,
      timestamp: Date.now(),
    }

    return this.setItem(key, JSON.stringify(expiringObject))
  }

  /**
   * Gets item and checks for expiry, automatically cleaning up expired items
   */
  static getItemWithExpiry(key: string): string | null {
    try {
      const item = localStorage.getItem(key)
      if (!item) return null

      const parsed: ExpiringStorageObject = JSON.parse(item)

      if (parsed.expiryStamp && Date.now() > parsed.expiryStamp) {
        localStorage.removeItem(key)
        return null
      }

      return parsed.value
    } catch {
      // If it's not a structured object, clean up and return null
      localStorage.removeItem(key)
      return null
    }
  }

  /**
   * Cleanup expired entries - called automatically during access
   */
  static cleanupExpired(): number {
    const entries = this.getAllEntries()
    let cleaned = 0

    for (const entry of entries) {
      if (entry.isExpired && !entry.isCritical) {
        try {
          localStorage.removeItem(entry.key)
          cleaned++
        } catch {
          // Ignore removal errors
        }
      }
    }

    return cleaned
  }

  /**
   * Get current storage statistics
   */
  static getStats() {
    const size = this.getStorageSize()
    const quota = this.getStorageQuota()
    const entries = this.getAllEntries()

    return {
      totalSize: size,
      quota: quota,
      usagePercent: (size / quota) * 100,
      entryCount: entries.length,
      expiredCount: entries.filter((e) => e.isExpired).length,
      criticalCount: entries.filter((e) => e.isCritical).length,
    }
  }
}

export default LocalStorageManager
